import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Plus, Edit2, MoreVertical, Search, Eye,
    CheckCircle, XCircle, Ban, RefreshCw, Star, Send, ShieldCheck
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Dropdown, DropdownItem, DropdownDivider,
    Modal, ConfirmDialog, toast, Tabs, Select
} from '../../components/common';
import { vendorApi } from '../../services/api';
import VendorForm from './VendorForm';
import VendorDetail from './VendorDetail';
import './Vendors.css';

export default function VendorList() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [viewingVendor, setViewingVendor] = useState(null);
    const [actionVendor, setActionVendor] = useState(null);
    const [actionType, setActionType] = useState('');

    // Fetch vendors
    const { data, isLoading } = useQuery({
        queryKey: ['vendors', page, pageSize, search, statusFilter],
        queryFn: () => vendorApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    // Status mutations
    const statusMutation = useMutation({
        mutationFn: ({ id, action, reason }) => {
            switch (action) {
                case 'submit': return vendorApi.submit(id);
                case 'verify': return vendorApi.verify(id);
                case 'approve': return vendorApi.approve(id);
                case 'suspend': return vendorApi.suspend(id, { reason });
                case 'blacklist': return vendorApi.blacklist(id, { reason });
                case 'reactivate': return vendorApi.reactivate(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Vendor ${action}d successfully`);
            queryClient.invalidateQueries(['vendors']);
            setActionVendor(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const handleEdit = (vendor) => {
        setEditingVendor(vendor);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingVendor(null);
    };

    const handleAction = (vendor, action) => {
        setActionVendor(vendor);
        setActionType(action);
    };

    const getStatusBadge = (status) => {
        const variants = {
            'pending_registration': 'warning',
            'pending_verification': 'warning',
            'verified': 'info',
            'active': 'success',
            'suspended': 'danger',
            'blacklisted': 'danger'
        };
        const labels = {
            'pending_registration': 'Pending Registration',
            'pending_verification': 'Pending Verification',
            'verified': 'Verified',
            'active': 'Active',
            'suspended': 'Suspended',
            'blacklisted': 'Blacklisted'
        };
        return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
    };

    const columns = [
        {
            key: 'code',
            title: 'Code',
            dataIndex: 'vendorCode',
            width: 120,
            render: (code) => <span className="vendor-code">{code}</span>
        },
        {
            key: 'name',
            title: 'Vendor Name',
            dataIndex: 'companyName',
            render: (companyName, row) => (
                <div className="vendor-cell">
                    <span className="vendor-name">{companyName || row.name}</span>
                    <span className="vendor-type">{row.vendorType}</span>
                </div>
            )
        },
        {
            key: 'contact',
            title: 'Contact',
            render: (_, row) => (
                <div className="vendor-cell">
                    <span>{row.contactPerson}</span>
                    <span className="vendor-email">{row.email}</span>
                </div>
            )
        },
        {
            key: 'rating',
            title: 'Rating',
            dataIndex: 'averageRating',
            width: 100,
            render: (rating) => (
                <div className="vendor-rating">
                    <Star size={14} className="star-icon" />
                    <span>{rating ? rating.toFixed(1) : '-'}</span>
                </div>
            )
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); setViewingVendor(row); }}>
                                View Details
                            </DropdownItem>
                            <DropdownItem icon={Edit2} onClick={() => { close(); handleEdit(row); }}>
                                Edit
                            </DropdownItem>
                            <DropdownDivider />
                            {row.status === 'pending_registration' && (
                                <DropdownItem icon={Send} onClick={() => { close(); handleAction(row, 'submit'); }}>
                                    Submit for Verification
                                </DropdownItem>
                            )}
                            {row.status === 'pending_verification' && (
                                <DropdownItem icon={ShieldCheck} onClick={() => { close(); handleAction(row, 'verify'); }}>
                                    Verify
                                </DropdownItem>
                            )}
                            {row.status === 'verified' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); handleAction(row, 'approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                            {row.status === 'active' && (
                                <>
                                    <DropdownItem icon={XCircle} onClick={() => { close(); handleAction(row, 'suspend'); }}>
                                        Suspend
                                    </DropdownItem>
                                    <DropdownItem icon={Ban} danger onClick={() => { close(); handleAction(row, 'blacklist'); }}>
                                        Blacklist
                                    </DropdownItem>
                                </>
                            )}
                            {(row.status === 'suspended' || row.status === 'blacklisted') && (
                                <DropdownItem icon={RefreshCw} onClick={() => { close(); handleAction(row, 'reactivate'); }}>
                                    Reactivate
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending_registration', label: 'Pending Registration' },
        { value: 'pending_verification', label: 'Pending Verification' },
        { value: 'verified', label: 'Verified' },
        { value: 'active', label: 'Active' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'blacklisted', label: 'Blacklisted' }
    ];

    return (
        <div className="vendors-page">
            <PageHeader
                title="Vendor Management"
                subtitle="Manage vendors and suppliers"
                breadcrumbs={[{ label: 'Vendors', path: '/vendors' }]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Add Vendor
                    </Button>
                }
            />

            <Card>
                <div className="vendors-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search vendors..."
                        className="vendors-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        placeholder="Filter by status"
                        className="vendors-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No vendors found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="vendors-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            {/* Vendor Form Modal */}
            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                size="xl"
            >
                <VendorForm
                    vendor={editingVendor}
                    onSuccess={() => {
                        handleCloseForm();
                        queryClient.invalidateQueries(['vendors']);
                    }}
                    onCancel={handleCloseForm}
                />
            </Modal>

            {/* Vendor Detail Modal */}
            <Modal
                isOpen={!!viewingVendor}
                onClose={() => setViewingVendor(null)}
                title="Vendor Details"
                size="lg"
            >
                {viewingVendor && <VendorDetail vendor={viewingVendor} />}
            </Modal>

            {/* Action Confirmation */}
            <ConfirmDialog
                isOpen={!!actionVendor}
                onClose={() => { setActionVendor(null); setActionType(''); }}
                onConfirm={() => statusMutation.mutate({
                    id: actionVendor?.id,
                    action: actionType
                })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Vendor`}
                message={`Are you sure you want to ${actionType} "${actionVendor?.companyName || actionVendor?.name}"?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={['suspend', 'blacklist'].includes(actionType) ? 'danger' : 'primary'}
                loading={statusMutation.isPending}
            />
        </div>
    );
}
